import {
  BadRequestError,
  ForbiddenError,
  HttpError,
  NetworkError,
  NotFoundError,
  ServerError,
  TimeoutError,
  UnauthorizedError,
  ValidationError,
} from './errors';

import type {
  HttpErrorResponse,
  ErrorWithCause,
  Primitive,
  QueryParams,
  RequestConfig,
} from './types';

export const DEFAULT_TIMEOUT_MS = 10_000;

export const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return Object.prototype.toString.call(value) === '[object Object]';
};

export const isJsonContentType = (contentType: string | null): boolean => {
  return Boolean(contentType && contentType.toLowerCase().includes('application/json'));
};

export const buildQueryString = (queryParams?: QueryParams): string => {
  if (!queryParams) {
    return '';
  }

  const searchParams = new URLSearchParams();

  Object.entries(queryParams).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      });
      return;
    }

    if (isPlainObject(value)) {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        if (nestedValue !== undefined && nestedValue !== null) {
          searchParams.append(`${key}[${nestedKey}]`, String(nestedValue));
        }
      });
      return;
    }

    searchParams.append(key, String(value as Primitive));
  });

  const queryString = searchParams.toString();

  return queryString ? `?${queryString}` : '';
};

export const buildUrl = (input: string, queryParams?: QueryParams): string => {
  return `${input}${buildQueryString(queryParams)}`;
};

export const createAbortController = (
  timeoutMs: number,
): {
  readonly controller: AbortController;
  readonly timeoutId: ReturnType<typeof setTimeout>;
} => {
  const controller = new AbortController();

  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  return { controller, timeoutId };
};

export const safeParseJson = async <T>(response: Response): Promise<T | null> => {
  const contentType = response.headers.get('content-type');

  if (!isJsonContentType(contentType)) {
    return null;
  }

  try {
    return (await response.json()) as T;
  } catch {
    return null;
  }
};

export const safeParseText = async (response: Response): Promise<string | null> => {
  try {
    return await response.text();
  } catch {
    return null;
  }
};

export const normalizeRequestBody = (
  body: RequestConfig['body'],
): {
  readonly body?: BodyInit;
  readonly contentType?: string;
} => {
  if (body === undefined) {
    return {};
  }

  if (body === null) {
    return { body: 'null', contentType: 'application/json' };
  }

  if (
    typeof body === 'string' ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    ArrayBuffer.isView(body) ||
    body instanceof ReadableStream
  ) {
    return { body: body as BodyInit };
  }

  if (isPlainObject(body)) {
    return {
      body: JSON.stringify(body),
      contentType: 'application/json',
    };
  }

  return { body: body as BodyInit };
};

export const buildErrorFromResponse = (
  status: number,
  payload: HttpErrorResponse | null,
  fallbackMessage: string,
): HttpError => {
  const message = payload?.message || fallbackMessage;

  const commonPayloadBase = {
    status,
    details: payload?.details ?? payload,
  };

  const commonPayload: HttpErrorResponse =
    payload?.code !== undefined ? { ...commonPayloadBase, code: payload.code } : commonPayloadBase;

  if (status === 400) {
    return new BadRequestError(message, commonPayload);
  }

  if (status === 401) {
    return new UnauthorizedError(message, commonPayload);
  }

  if (status === 403) {
    return new ForbiddenError(message, commonPayload);
  }

  if (status === 404) {
    return new NotFoundError(message, commonPayload);
  }

  if (status === 409 || status === 422) {
    return new ValidationError(message, commonPayload);
  }

  if (status >= 500) {
    return new ServerError(message, commonPayload);
  }

  return new HttpError(message, commonPayload);
};

export const normalizeUnknownError = (error: unknown): HttpError => {
  if (error instanceof HttpError) {
    return error;
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    return new TimeoutError();
  }

  const errorWithCause = error as ErrorWithCause;

  return new NetworkError(
    error instanceof Error ? error.message : 'Unexpected network error',
    errorWithCause?.cause ?? error,
  );
};

export const isNetworkError = (error: unknown): error is NetworkError => {
  return error instanceof NetworkError;
};
